import React, { useState } from 'react'
import styled from 'styled-components'
import {
  Grid,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  CircularProgress,
  Divider,
  Select,
  MenuItem,
  Input,
  Checkbox,
  ListItemText,
  InputLabel,
  FormControl,
} from '@material-ui/core'
import PlayArrowIcon from '@material-ui/icons/PlayArrow'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import ErrorIcon from '@material-ui/icons/Error'
import RefreshIcon from '@material-ui/icons/Refresh'
import IconButton from '@material-ui/core/IconButton'
import {
  ProductMigrationStep,
  ProductMigrationStepStatus,
  ProductMigrationState,
  ProductMigrationStepInfo,
  EProductExportDedupeKey,
} from '../../../../types/migration'
import { ErrorDisplay } from './ErrorDisplay'
import { ConfirmFixDuplicatesModal } from './ConfirmFixDuplicatesModal'

// Styled Components
const HeaderText = styled(Typography)`
  font-weight: 600;
  color: #333;
`

const StyledMigrateButton = styled(Button)`
  min-width: 120px;
  text-transform: none !important;
  background-color: #e3f2fd;
  color: #1976d2;
  font-weight: 500;

  &:hover {
    background-color: #bbdefb;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(33, 150, 243, 0.4);
  }

  &:disabled {
    background-color: #f5f5f5;
    color: #9e9e9e;
    transform: none;
    box-shadow: none;
  }
`

const StepContainer = styled.div`
  padding: 8px 0;
  display: flex;
  align-items: center;
`

interface StepNumberProps {
  active?: boolean
  completed?: boolean
  error?: boolean
}

const StepNumber = styled.div<StepNumberProps>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  margin-right: 16px;
  font-size: 14px;
  background-color: ${(props: StepNumberProps): string =>
    props.completed ? '#4caf50' : props.error ? '#f44336' : props.active ? '#2196f3' : '#e0e0e0'};
  color: ${(props: StepNumberProps): string =>
    props.completed || props.error || props.active ? 'white' : '#757575'};
`

const StepContent = styled.div`
  flex: 1;
`

const WarningChip = styled(Chip)`
  && {
    background-color: #fff3e0;
    color: #e65100;
    font-weight: 500;
    margin: 8px 0;
  }
`

export const productMigrationReps = {
  [ProductMigrationStep.PREVALIDATE]: {
    label: 'Prevalidate',
    description: 'Validate product data before deduplication',
  },
  [ProductMigrationStep.DEDUPE_VALIDATE]: {
    label: 'Validate & Deduplicate',
    description: 'Validate product data and remove duplicates',
  },
  [ProductMigrationStep.BULK_CREATE]: {
    label: 'Create Products',
    description: 'Create products in the system',
  },
  [ProductMigrationStep.VALIDATE_MIGRATION]: {
    label: 'Validate Migration',
    description: 'Validate the migrated products for consistency',
  },
  [ProductMigrationStep.UPDATE_PLATFORM_IDS]: {
    label: 'Update Platform IDs',
    description: 'Update shop platform product identifiers',
  },
  [ProductMigrationStep.SYNC_OPEN_SEARCH]: {
    label: 'Sync Open Search',
    description: 'Synchronize products with Open Search',
  },
}

// Helper functions
const getStatusChip = (status: ProductMigrationStepStatus): JSX.Element => {
  const statusProps = {
    [ProductMigrationStepStatus.DONE]: {
      label: 'Completed',
      style: { backgroundColor: '#e8f5e8', color: '#2e7d32' },
    },
    [ProductMigrationStepStatus.FAILED]: {
      label: 'Error',
      style: { backgroundColor: '#ffebee', color: '#c62828' },
    },
    [ProductMigrationStepStatus.IN_PROGRESS]: {
      label: 'Running',
      style: { backgroundColor: '#e3f2fd', color: '#1565c0' },
    },
    [ProductMigrationStepStatus.NOT_STARTED]: {
      label: 'Pending',
      style: { backgroundColor: '#f5f5f5', color: '#757575' },
    },
  }

  return (
    <Chip
      label={statusProps[status]?.label || 'Unknown'}
      style={statusProps[status]?.style || { backgroundColor: '#f5f5f5', color: '#757575' }}
    />
  )
}

// Component Props
interface ProductMigrationGroupProps {
  isLoading?: boolean
  isCompanySelected?: boolean
  hasSelectedShops?: boolean
  hasValidSnapshot?: boolean
  areDependenciesMigrated?: boolean
  onStartPrevalidate?: () => void
  onStartDedupeValidate?: () => void
  onStartBulkCreate?: () => void
  onStartUpdatePlatformIds?: () => void
  onStartValidateMigration: () => void
  onStartSyncOpenSearch?: () => void
  onRefreshStatus?: () => void
  onToggleEvents?: () => void
  areEventsEnabled?: boolean | undefined
  isTogglingEvents?: boolean
  migrationState?: ProductMigrationState
  dedupeKeys: EProductExportDedupeKey[]
  setDedupeKeys: React.Dispatch<React.SetStateAction<EProductExportDedupeKey[]>>
  handleFixDuplicateProducts: () => void
}

interface ProductMigrationModalProps {
  open: boolean
  onClose: () => void
  hasValidSnapshot: boolean
  onStartPrevalidate?: () => void
  onStartDedupeValidate?: () => void
  onStartBulkCreate?: () => void
  onStartUpdatePlatformIds?: () => void
  onStartValidateMigration: () => void
  onStartSyncOpenSearch?: () => void
  onRefreshStatus?: () => void
  onToggleEvents?: () => void
  areEventsEnabled?: boolean | undefined
  isTogglingEvents?: boolean
  migrationState: ProductMigrationState
  areDependenciesMigrated: boolean
  dedupeKeys: EProductExportDedupeKey[]
  setDedupeKeys: React.Dispatch<React.SetStateAction<EProductExportDedupeKey[]>>
  handleFixDuplicateProducts: () => void
}

const ProductMigrationModal: React.FC<ProductMigrationModalProps> = ({
  open,
  onClose,
  hasValidSnapshot,
  onStartPrevalidate,
  onStartDedupeValidate,
  onStartBulkCreate,
  onStartUpdatePlatformIds,
  onStartValidateMigration,
  onStartSyncOpenSearch,
  onRefreshStatus,
  onToggleEvents,
  areEventsEnabled,
  isTogglingEvents,
  migrationState,
  areDependenciesMigrated,
  dedupeKeys,
  setDedupeKeys,
  handleFixDuplicateProducts,
}) => {
  const [fixingDuplicatesOpen, setFixingDuplicatesOpen] = useState(false)

  const handleOpenFixDuplicates = () => {
    setFixingDuplicatesOpen(true)
  }

  const handleCloseDuplicateModal = () => {
    setFixingDuplicatesOpen(false)
  }
  // Helper function to check if a step can be executed
  const isStepEnabled = (stepIndex: number): boolean => {
    if (migrationState.isRunning) return false

    const steps = migrationState.steps
    const currentStepInfo = steps[stepIndex]

    switch (currentStepInfo.step) {
      case ProductMigrationStep.PREVALIDATE:
        return true

      case ProductMigrationStep.DEDUPE_VALIDATE: {
        const dedupeStep = steps.find((s) => s.step === ProductMigrationStep.PREVALIDATE)
        return dedupeStep?.status === ProductMigrationStepStatus.DONE
      }

      case ProductMigrationStep.BULK_CREATE: {
        const dedupeStep = steps.find((s) => s.step === ProductMigrationStep.DEDUPE_VALIDATE)
        return dedupeStep?.status === ProductMigrationStepStatus.DONE
      }

      case ProductMigrationStep.VALIDATE_MIGRATION:
      case ProductMigrationStep.UPDATE_PLATFORM_IDS: {
        const bulkCreateStep = steps.find((s) => s.step === ProductMigrationStep.BULK_CREATE)
        return bulkCreateStep?.status === ProductMigrationStepStatus.DONE
      }

      case ProductMigrationStep.SYNC_OPEN_SEARCH: {
        const validateStep = steps.find((s) => s.step === ProductMigrationStep.VALIDATE_MIGRATION)
        const updateIdsStep = steps.find((s) => s.step === ProductMigrationStep.UPDATE_PLATFORM_IDS)
        return (
          validateStep?.status === ProductMigrationStepStatus.DONE &&
          updateIdsStep?.status === ProductMigrationStepStatus.DONE
        )
      }

      default:
        return false
    }
  }

  const getStepHandler = (step: ProductMigrationStep): (() => void) | undefined => {
    switch (step) {
      case ProductMigrationStep.PREVALIDATE:
        return onStartPrevalidate
      case ProductMigrationStep.DEDUPE_VALIDATE:
        return onStartDedupeValidate
      case ProductMigrationStep.BULK_CREATE:
        return onStartBulkCreate
      case ProductMigrationStep.UPDATE_PLATFORM_IDS:
        return onStartUpdatePlatformIds
      case ProductMigrationStep.VALIDATE_MIGRATION:
        return onStartValidateMigration
      case ProductMigrationStep.SYNC_OPEN_SEARCH:
        return onStartSyncOpenSearch
      default:
        return undefined
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography component="h2" style={{ fontSize: '1.25rem', fontWeight: 600 }}>
            Product Migration Process
          </Typography>
          {onRefreshStatus && (
            <IconButton
              onClick={onRefreshStatus}
              title="Refresh Status"
              style={{ marginLeft: 8, padding: 8 }}
            >
              <RefreshIcon style={{ fontSize: 20 }} />
            </IconButton>
          )}
        </div>
      </DialogTitle>
      <DialogContent>
        {/* Product Events Banner */}
        {areEventsEnabled !== undefined && (
          <div
            style={{
              padding: '12px 16px',
              marginBottom: 16,
              borderRadius: 4,
              backgroundColor: areEventsEnabled ? '#ffebee' : '#e8f5e8',
              border: `1px solid ${areEventsEnabled ? '#f44336' : '#4caf50'}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div>
              <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 4 }}>
                {areEventsEnabled ? '⚠️ Product Events: Enabled' : '✅ Product Events: Disabled'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {areEventsEnabled
                  ? 'All migration steps are blocked while events are enabled'
                  : 'Migration steps can be executed normally'}
              </Typography>
            </div>
            {onToggleEvents && (
              <Button
                size="small"
                variant="outlined"
                disabled={isTogglingEvents}
                onClick={onToggleEvents}
                style={{
                  marginLeft: 16,
                  borderColor: areEventsEnabled ? '#4caf50' : '#f44336',
                  color: areEventsEnabled ? '#4caf50' : '#f44336',
                }}
              >
                {isTogglingEvents
                  ? 'Loading...'
                  : areEventsEnabled
                  ? 'Disable Events'
                  : 'Enable Events'}
              </Button>
            )}
          </div>
        )}

        {/* Snapshot Validation Banner */}
        {!hasValidSnapshot && (
          <div
            style={{
              padding: '12px 16px',
              marginBottom: 16,
              borderRadius: 4,
              backgroundColor: '#fff3cd',
              border: '1px solid #ffc107',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div>
              <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 4 }}>
                ⚠️ Snapshot Required
              </Typography>
              <Typography variant="body2" color="textSecondary">
                A valid company products snapshot (generated within the last hour) is required for
                migration
              </Typography>
            </div>
          </div>
        )}

        {!areDependenciesMigrated && (
          <WarningChip label="⚠️ Please migrate Brands, Vendors, Categories, and Pricing Templates first" />
        )}

        <div style={{ marginTop: 16 }}>
          {migrationState.steps.map((stepInfo: ProductMigrationStepInfo, index: number) => {
            const isActive = stepInfo.status === ProductMigrationStepStatus.IN_PROGRESS
            const isCompleted = stepInfo.status === ProductMigrationStepStatus.DONE
            const isError = stepInfo.status === ProductMigrationStepStatus.FAILED
            const canExecute = isStepEnabled(index)
            const stepHandler = getStepHandler(stepInfo.step)

            return (
              <div key={stepInfo.step}>
                <StepContainer>
                  <StepNumber active={isActive} completed={isCompleted} error={isError}>
                    {isActive ? (
                      <CircularProgress size={16} style={{ color: 'white' }} />
                    ) : isCompleted ? (
                      <CheckCircleIcon style={{ fontSize: 20, color: 'white' }} />
                    ) : isError ? (
                      <ErrorIcon style={{ fontSize: 20, color: 'white' }} />
                    ) : (
                      index + 1
                    )}
                  </StepNumber>

                  <StepContent>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                      <div style={{ flex: 1 }}>
                        <Typography
                          component="h3"
                          style={{ fontSize: '1rem', fontWeight: 500, marginBottom: 4 }}
                        >
                          {productMigrationReps[stepInfo.step].label}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {productMigrationReps[stepInfo.step].description}
                        </Typography>
                      </div>

                      <div
                        style={{ marginLeft: 16, display: 'flex', alignItems: 'center', gap: 8 }}
                      >
                        {getStatusChip(stepInfo.status)}

                        {stepInfo.status === ProductMigrationStepStatus.NOT_STARTED && (
                          <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            disabled={!canExecute || areEventsEnabled === true || !hasValidSnapshot}
                            onClick={stepHandler}
                            style={{ minWidth: 80 }}
                          >
                            <PlayArrowIcon style={{ fontSize: 14, marginRight: 4 }} />
                            Start
                          </Button>
                        )}

                        {stepInfo.status === ProductMigrationStepStatus.FAILED && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="secondary"
                            disabled={!canExecute || areEventsEnabled === true || !hasValidSnapshot}
                            onClick={stepHandler}
                            style={{ minWidth: 80 }}
                          >
                            Retry
                          </Button>
                        )}
                      </div>
                    </div>
                    {(stepInfo.step === ProductMigrationStep.DEDUPE_VALIDATE ||
                      stepInfo.step === ProductMigrationStep.PREVALIDATE ||
                      stepInfo.step === ProductMigrationStep.VALIDATE_MIGRATION) && (
                      <FormControl style={{ margin: '8px 0', minWidth: 240 }}>
                        <InputLabel>Dedupe Keys</InputLabel>
                        <Select
                          multiple
                          value={dedupeKeys}
                          onChange={(e) =>
                            setDedupeKeys(e.target.value as unknown as EProductExportDedupeKey[])
                          }
                          input={<Input />}
                          renderValue={(selected) => (selected as string[]).join(', ')}
                        >
                          {Object.values(EProductExportDedupeKey).map((key) => (
                            <MenuItem key={key} value={key}>
                              <Checkbox checked={dedupeKeys.indexOf(key) > -1} />
                              <ListItemText primary={key} />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                    {stepInfo.message && <ErrorDisplay message={stepInfo.message} />}
                    {stepInfo.message && stepInfo.step === ProductMigrationStep.PREVALIDATE && (
                      <Button
                        size="small"
                        variant="contained"
                        color="secondary"
                        onClick={handleOpenFixDuplicates}
                        style={{ minWidth: 80, marginTop: 8 }}
                      >
                        Fix Automatically
                      </Button>
                    )}
                  </StepContent>
                </StepContainer>
                {index < migrationState.steps.length - 1 && (
                  <Divider style={{ margin: '16px 0' }} />
                )}
              </div>
            )
          })}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="default">
          Close
        </Button>
      </DialogActions>
      <ConfirmFixDuplicatesModal
        open={fixingDuplicatesOpen}
        onClose={handleCloseDuplicateModal}
        handleFixDuplicateProducts={handleFixDuplicateProducts}
      />
    </Dialog>
  )
}

export const defaultMigrationState: ProductMigrationState = {
  steps: [
    {
      step: ProductMigrationStep.PREVALIDATE,
      status: ProductMigrationStepStatus.NOT_STARTED,
    },
    {
      step: ProductMigrationStep.DEDUPE_VALIDATE,
      status: ProductMigrationStepStatus.NOT_STARTED,
    },
    {
      step: ProductMigrationStep.BULK_CREATE,
      status: ProductMigrationStepStatus.NOT_STARTED,
    },
    {
      step: ProductMigrationStep.VALIDATE_MIGRATION,
      status: ProductMigrationStepStatus.NOT_STARTED,
    },
    {
      step: ProductMigrationStep.UPDATE_PLATFORM_IDS,
      status: ProductMigrationStepStatus.NOT_STARTED,
    },
    {
      step: ProductMigrationStep.SYNC_OPEN_SEARCH,
      status: ProductMigrationStepStatus.NOT_STARTED,
    },
  ],
  currentStep: 0,
  isRunning: false,
}

export const ProductMigrationGroup: React.FC<ProductMigrationGroupProps> = ({
  isLoading = false,
  isCompanySelected = false,
  hasSelectedShops = false,
  hasValidSnapshot = true,
  areDependenciesMigrated = false,
  onStartPrevalidate,
  onStartDedupeValidate,
  onStartBulkCreate,
  onStartUpdatePlatformIds,
  onStartValidateMigration,
  onStartSyncOpenSearch,
  onRefreshStatus,
  onToggleEvents,
  areEventsEnabled,
  isTogglingEvents,
  migrationState,
  dedupeKeys,
  setDedupeKeys,
  handleFixDuplicateProducts,
}) => {
  const [modalOpen, setModalOpen] = useState(false)
  const [syncConfirmationOpen, setSyncConfirmationOpen] = useState(false)

  // Handle sync confirmation for Step 4
  const handleSyncOpenSearchClick = () => {
    setSyncConfirmationOpen(true)
  }

  const handleConfirmSync = () => {
    setSyncConfirmationOpen(false)
    if (onStartSyncOpenSearch) {
      onStartSyncOpenSearch()
    }
  }

  const handleCancelSync = () => {
    setSyncConfirmationOpen(false)
  }

  const currentState = migrationState || defaultMigrationState
  const canOpenModal = isCompanySelected && hasSelectedShops && !isLoading && hasValidSnapshot
  const isAnyStepCompleted = currentState.steps.some(
    (step: ProductMigrationStepInfo) => step.status === ProductMigrationStepStatus.DONE,
  )

  const getOverallStatus = (): string => {
    if (currentState.isRunning) return 'In Progress'
    if (
      currentState.steps.every(
        (step: ProductMigrationStepInfo) => step.status === ProductMigrationStepStatus.DONE,
      )
    ) {
      return 'Completed'
    }
    if (
      currentState.steps.some(
        (step: ProductMigrationStepInfo) => step.status === ProductMigrationStepStatus.FAILED,
      )
    ) {
      return 'Error'
    }
    if (isAnyStepCompleted) return 'Partially Completed'
    return 'Not Started'
  }

  const handleOpenModal = (): void => {
    if (canOpenModal) {
      setModalOpen(true)
      // Auto-refresh status when modal opens
      if (onRefreshStatus) {
        onRefreshStatus()
      }
    }
  }

  return (
    <Grid container direction="column">
      <Grid item>
        <Grid container style={{ padding: '8px 0', alignItems: 'center' }}>
          <Grid item xs={6} sm={4}>
            <HeaderText>Products (Advanced)</HeaderText>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="body2" style={{ fontWeight: 500 }}>
              {getOverallStatus()}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={4}>
            <StyledMigrateButton size="small" onClick={handleOpenModal} disabled={!canOpenModal}>
              Migrate Products
            </StyledMigrateButton>
          </Grid>
        </Grid>
      </Grid>

      {!areDependenciesMigrated && isCompanySelected && (
        <Grid item>
          <WarningChip label="⚠️ Migrate other collections first (Brands, Vendors, Categories, Pricing Templates)" />
        </Grid>
      )}

      {!hasValidSnapshot && isCompanySelected && (
        <Grid item>
          <WarningChip label="⚠️ Valid products snapshot required (generate one within the last hour)" />
        </Grid>
      )}

      <ProductMigrationModal
        open={modalOpen}
        onClose={(): void => setModalOpen(false)}
        hasValidSnapshot={hasValidSnapshot}
        onStartPrevalidate={onStartPrevalidate}
        onStartDedupeValidate={onStartDedupeValidate}
        onStartBulkCreate={onStartBulkCreate}
        onStartUpdatePlatformIds={onStartUpdatePlatformIds}
        onStartValidateMigration={onStartValidateMigration}
        onStartSyncOpenSearch={handleSyncOpenSearchClick}
        onRefreshStatus={onRefreshStatus}
        onToggleEvents={onToggleEvents}
        areEventsEnabled={areEventsEnabled}
        isTogglingEvents={isTogglingEvents}
        migrationState={currentState}
        areDependenciesMigrated={areDependenciesMigrated}
        dedupeKeys={dedupeKeys}
        setDedupeKeys={setDedupeKeys}
        handleFixDuplicateProducts={handleFixDuplicateProducts}
      />

      {/* Sync Confirmation Modal */}
      <Dialog open={syncConfirmationOpen} onClose={handleCancelSync} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography
            component="h2"
            style={{ fontSize: '1.25rem', fontWeight: 600, color: '#d32f2f' }}
          >
            ⚠️ Open Search Sync Confirmation
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" style={{ marginBottom: 16, lineHeight: 1.6 }}>
            <strong>Warning:</strong> Open Search synchronizations are risky operations that should
            not be executed during business hours.
          </Typography>
          <Typography variant="body2" style={{ marginBottom: 12, color: '#666', lineHeight: 1.6 }}>
            • If any products are updated while the sync is running, it will create conflicts and
            stop the process
          </Typography>
          <Typography variant="body2" style={{ marginBottom: 16, color: '#666', lineHeight: 1.6 }}>
            • Recommended execution time: during nights or when the company/shops are closed
          </Typography>
          <Typography variant="body1" style={{ fontWeight: 600, color: '#d32f2f' }}>
            Are you sure you want to proceed with the Open Search sync?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelSync} color="default">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmSync}
            color="primary"
            variant="contained"
            style={{ backgroundColor: '#d32f2f', color: 'white' }}
          >
            Proceed with Sync
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  )
}



// WEBPACK FOOTER //
// ./src/containers/Settings/Development/MigrateCompanyProducts/ProductMigrationGroup.tsx